import { SimpleTooltip } from "Shared/SimpleTooltip";
import { cn } from "@/lib/utils";

type Props = {
	initials: string;
	name: string;
	tenantName?: string;
	/** When set, the avatar gets this as a hover tooltip (the user's login). */
	avatarTooltip?: string;
	/** Avatar + text density: `md` for the drawer footer, `sm` for the topbar. */
	size?: "sm" | "md";
	/** Hide the name/tenant text below this breakpoint (avatar stays). */
	hideTextBelow?: "xl";
	/** Width control for the text block, e.g. `flex-1` or `max-w-[160px]`. */
	textClassName?: string;
};

/**
 * Circular initials avatar next to a two-line identity (name over tenant).
 * Shared by the drawer footer and the topbar user menu so avatar styling and
 * the name/tenant layout stay consistent; callers tune density via `size`,
 * width via `textClassName` and responsive visibility via `hideTextBelow`.
 */
export const UserIdentity = ({
	initials,
	name,
	tenantName,
	avatarTooltip,
	size = "md",
	hideTextBelow,
	textClassName,
}: Props) => {
	const avatar = (
		<div
			className={cn(
				"bg-primary-light text-primary flex flex-shrink-0 items-center justify-center rounded-full font-semibold",
				size === "md" ? "text-body h-8 w-8" : "text-caption h-7 w-7",
			)}
		>
			{initials}
		</div>
	);

	return (
		<>
			{avatarTooltip ? (
				<SimpleTooltip content={avatarTooltip} delay={0}>
					{avatar}
				</SimpleTooltip>
			) : (
				avatar
			)}
			<div
				className={cn(
					"min-w-0 flex-col leading-tight",
					hideTextBelow === "xl" ? "hidden xl:flex" : "flex",
					textClassName,
				)}
			>
				<span
					className={cn(
						"text-foreground truncate font-medium",
						size === "md" ? "text-body" : "text-caption",
					)}
				>
					{name}
				</span>
				{tenantName ? (
					<span className="text-micro text-muted-foreground truncate">
						{tenantName}
					</span>
				) : null}
			</div>
		</>
	);
};
